import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import {
  STAGED_ACTIONS_QUERY_KEY,
  type StagedAction,
  type StagedActionEditOptions,
  type UpdateStagedActionView,
} from './use-staged-actions'
import { STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY } from '../constants'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { applyOptimisticTagEdits } from './optimistic-tag-edits'
import { mergeStagedActionSnapshots, stagedActionExpandedQueryKey } from './staged-actions/utils'

export function useUpdateStagedActionView() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({ id, view }: { id: string; view: UpdateStagedActionView }) => {
      const token = await getAuthToken()
      const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ view }),
      })
      if (!response.ok) throw new Error('Failed to update staged action view')
      const json = await response.json()
      return json.data as StagedAction
    },
    onMutate: async ({ id, view }) => {
      await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      const previous = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
      const expandedQueryKey = stagedActionExpandedQueryKey(id)
      const previousExpanded = queryClient.getQueryData<StagedAction | null>(expandedQueryKey)

      const editOptions = queryClient.getQueryData<StagedActionEditOptions>([STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY, id])
      const previousEditOptions = editOptions

      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) =>
          old?.map((action) => {
            if (action.id !== id || !action.view) return action
            const updatedView = { ...action.view }

            if (view.title !== undefined) {
              const currentCoreTitle = editOptions?.title?.coreTitle
              updatedView.title = currentCoreTitle
                ? action.view.title.replace(currentCoreTitle, view.title)
                : view.title
            }

            if (view.content !== undefined) {
              updatedView.content = view.content
            }

            if (view.tagEdits && editOptions?.tags) {
              updatedView.tags = applyOptimisticTagEdits(action.view.tags, view.tagEdits, editOptions.tags)
            }

            return {
              ...action,
              view: updatedView,
              metadata: { ...action.metadata, userEdited: 'true' },
            }
          }) ?? [],
      )

      const optimisticAction = queryClient
        .getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
        ?.find((action) => action.id === id)
      if (optimisticAction) {
        queryClient.setQueryData(expandedQueryKey, mergeStagedActionSnapshots(previousExpanded, optimisticAction))
      }

      if (view.title !== undefined && editOptions?.title) {
        queryClient.setQueryData<StagedActionEditOptions>([STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY, id], {
          ...editOptions,
          title: { coreTitle: view.title },
        })
      }

      return { previous, previousEditOptions, previousExpanded }
    },
    onError: (_err, { id }, context) => {
      if (context?.previous) {
        queryClient.setQueryData([STAGED_ACTIONS_QUERY_KEY], context.previous)
      }
      if (context?.previousExpanded !== undefined) {
        queryClient.setQueryData(stagedActionExpandedQueryKey(id), context.previousExpanded)
      }
      if (context?.previousEditOptions !== undefined) {
        queryClient.setQueryData([STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY, id], context.previousEditOptions)
      }
    },
    onSuccess: (updatedAction, { id, view }) => {
      // Version-skew insurance: the backend now returns the PATCH response
      // expanded (with `conversation` metadata), but against an older gateway it's
      // bare, so saving an edit would drop chat attribution until the next
      // expanded refetch. Read the expanded cache first — that's where the
      // metadata lives, since list rows are lean — and let the merge carry the
      // conversation over while the action still points at it. Deletable once
      // expanded PATCH responses are universally deployed.
      const previousExpanded =
        queryClient.getQueryData<StagedAction | null>(stagedActionExpandedQueryKey(updatedAction.id)) ??
        queryClient
          .getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
          ?.find((a) => a.id === updatedAction.id)
      const merged = mergeStagedActionSnapshots(previousExpanded, updatedAction) ?? updatedAction
      queryClient.setQueryData<Array<StagedAction>>(
        [STAGED_ACTIONS_QUERY_KEY],
        (old) => old?.map((a) => (a.id === merged.id ? merged : a)) ?? [],
      )
      queryClient.setQueryData(stagedActionExpandedQueryKey(merged.id), merged)
      AmplitudeRenderer.trackEvent('Staged Action', {
        action: 'edit',
        ...stagedActionEventProperties(updatedAction),
        actionId: id,
        editMethod: 'inline',
        editedTitle: view.title !== undefined,
        editedContent: view.content !== undefined,
        editedTags: view.tagEdits !== undefined,
      })
      // Refetch this action's edit options so the popover re-seeds from the
      // server-normalized payload on reopen instead of the stale original value.
      // The editable-datetime consumer only re-seeds its draft while CLOSED
      // (`if (!open && currentSeed !== lastSeedRef.current) setDraft(...)`), so a
      // refresh while the popover is open won't clobber an in-progress edit.
      // This also keeps the Guest multiText `options` (which map a token's display
      // label back to its submit value/email) in sync after an attendee add/remove,
      // so the next edit derives from the current attendee set, not a stale snapshot.
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTION_EDIT_OPTIONS_QUERY_KEY, updatedAction.id] })
    },
  })

  return {
    updateView: useCallback(
      (id: string, view: UpdateStagedActionView) => mutation.mutateAsync({ id, view }),
      [mutation.mutateAsync],
    ),
    isUpdating: mutation.isPending,
  }
}
